import { useSyncExternalStore } from 'react';

export const MOBILE_BREAKPOINT = 768;

export interface ResponsiveSnapshot {
  width: number;
  height: number;
  isMobile: boolean;
}

const defaultSnapshot: ResponsiveSnapshot = {
  width: 0,
  height: 0,
  isMobile: false
};

let cachedSnapshot: ResponsiveSnapshot = defaultSnapshot;

const computeSnapshot = (): ResponsiveSnapshot => {
  if (typeof window === 'undefined') {
    return cachedSnapshot;
  }

  const width = window.innerWidth;
  const height = window.innerHeight;
  const isMobile = width <= MOBILE_BREAKPOINT;

  if (
    cachedSnapshot.width !== width ||
    cachedSnapshot.height !== height ||
    cachedSnapshot.isMobile !== isMobile
  ) {
    cachedSnapshot = { width, height, isMobile };
  }

  if (typeof document !== 'undefined') {
    const target = document.documentElement;
    const expectedValue = isMobile ? 'mobile' : 'desktop';
    if (target.dataset.viewport !== expectedValue) {
      target.dataset.viewport = expectedValue;
    }
  }

  return cachedSnapshot;
};

const getSnapshot = (): ResponsiveSnapshot => computeSnapshot();

const getServerSnapshot = (): ResponsiveSnapshot => defaultSnapshot;

const subscribe = (callback: () => void): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const handler = () => {
    const snapshotBefore = cachedSnapshot;
    const snapshotAfter = computeSnapshot();
    if (
      snapshotBefore.width !== snapshotAfter.width ||
      snapshotBefore.height !== snapshotAfter.height ||
      snapshotBefore.isMobile !== snapshotAfter.isMobile
    ) {
      callback();
    }
  };

  window.addEventListener('resize', handler, { passive: true } as AddEventListenerOptions);
  window.addEventListener('orientationchange', handler);

  return () => {
    window.removeEventListener('resize', handler);
    window.removeEventListener('orientationchange', handler);
  };
};

export const useResponsive = (): ResponsiveSnapshot => {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
};

export const getResponsiveSnapshot = (): ResponsiveSnapshot => computeSnapshot();

if (typeof window !== 'undefined') {
  computeSnapshot();
}

